import { useMemo } from 'react';
import { faArrowRotateRight } from '@fortawesome/pro-regular-svg-icons/faArrowRotateRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Alert, Stack, Text, Title } from '@mantine/core';
import { clsx } from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';

import { wizardRatesPanelMessages } from './wizard-rates-panel-messages';
import { WizardCostBreakdown } from './WizardCostBreakdown';
import { WizardFreightRateCard } from './WizardFreightRateCard';
import { WizardFreightRateCardSkeleton } from './WizardFreightRateCardSkeleton';
import { WizardInsuranceFooter } from './WizardInsuranceFooter';
import styles from './WizardRatesPanel.module.css';
import { FREIGHT_QUOTE_ERROR_CODE } from '../../../../ltl/constants';
import { FreightNoCarriersMessage } from '../../../freight-no-carriers-message/freight-no-carriers-message';
import { groupRates } from '../../../freight-rate-card/rate-grouping';

import type { LtlAddress, LtlQuote } from '../../../../types/ltl';
import type { ShipFromAddress } from '../../../../types/ship-from';
import type { WizardInsurance } from '../../freight-booking-wizard-types';

const SKELETON_ROW_COUNT = 3;

type WizardRatesPanelProps = {
  rates: LtlQuote[];
  isLoadingRates: boolean;
  rateError?: string;
  rateErrorCode?: string | null;
  selectedRate: LtlQuote | null;
  onSelectRate: (quote: LtlQuote) => void;
  onRefresh: () => void;
  insurance: WizardInsurance;
  onInsuranceChange: (next: WizardInsurance) => void;
  shipFromAddress: ShipFromAddress;
  destinationAddress: LtlAddress;
  isReadyToQuote?: boolean;
  zIndex?: number;
};

export const WizardRatesPanel = ({
  rates,
  isLoadingRates,
  rateError,
  rateErrorCode,
  selectedRate,
  onSelectRate,
  onRefresh,
  insurance,
  onInsuranceChange,
  shipFromAddress,
  destinationAddress,
  isReadyToQuote = false,
  zIndex,
}: WizardRatesPanelProps) => {
  const intl = useIntl();

  const grouped = useMemo(() => groupRates(rates), [rates]);
  const carrierRows = insurance.isInsured ? grouped.withInsurance : grouped.withoutInsurance;
  const hasRates = carrierRows.length > 0;
  const isQuoteIneligible = rateErrorCode === FREIGHT_QUOTE_ERROR_CODE.QuoteIneligible;

  return (
    <div className={styles.panel}>
      <div className={styles.scrollArea}>
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <Title fw={600} order={3}>
              {intl.formatMessage(wizardRatesPanelMessages.title)}
            </Title>
            <ActionIcon
              aria-label={intl.formatMessage(wizardRatesPanelMessages.refreshAriaLabel)}
              disabled={isLoadingRates || !isReadyToQuote}
              onClick={onRefresh}
              variant='ghost'
            >
              <FontAwesomeIcon
                className={clsx(styles.refreshIcon, isLoadingRates && styles.refreshIconSpinning)}
                icon={faArrowRotateRight}
              />
            </ActionIcon>
          </div>
        </div>
        {(isLoadingRates || hasRates) && (
          <div className={styles.columnHeader} data-testid='wizard-rates-column-header'>
            <div className={styles.columnHeaderSpacer} />
            <Text className={styles.columnHeaderLabel} component='span' fw={600} size='sm'>
              {intl.formatMessage(wizardRatesPanelMessages.standardColumnHeader)}
            </Text>
            <Text className={styles.columnHeaderLabel} component='span' fw={600} size='sm'>
              {intl.formatMessage(wizardRatesPanelMessages.guaranteedColumnHeader)}
            </Text>
          </div>
        )}
        <Stack className={styles.body} gap='md'>
          {rateError && !isQuoteIneligible && (
            <Alert color='red' data-testid='wizard-rate-error'>
              {rateError}
            </Alert>
          )}
          {isLoadingRates && (
            <div className={styles.ratesList}>
              {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
                <WizardFreightRateCardSkeleton key={`wizard-rate-skeleton-${index}`} />
              ))}
            </div>
          )}
          {!isLoadingRates && isQuoteIneligible && !hasRates && (
            <div className={styles.emptyState}>
              <FreightNoCarriersMessage />
            </div>
          )}
          {!isLoadingRates && !hasRates && !rateError && !isQuoteIneligible && !isReadyToQuote && (
            <Text className={styles.emptyState} c='dimmed' data-testid='wizard-rates-incomplete'>
              {intl.formatMessage(wizardRatesPanelMessages.completeRequiredFields)}
            </Text>
          )}
          {!isLoadingRates && !hasRates && !rateError && !isQuoteIneligible && isReadyToQuote && (
            <Text className={styles.emptyState} c='dimmed' data-testid='wizard-rates-empty'>
              {intl.formatMessage(wizardRatesPanelMessages.noRates)}
            </Text>
          )}
          {!isLoadingRates && hasRates && (
            <div className={styles.ratesList}>
              {carrierRows.map((carrierRow) => (
                <WizardFreightRateCard
                  carrierRow={carrierRow}
                  destinationAddress={destinationAddress}
                  key={carrierRow.carrierKey}
                  onSelectRate={onSelectRate}
                  selectedOfferId={selectedRate?.offerId}
                  shipFromAddress={shipFromAddress}
                  zIndex={zIndex}
                />
              ))}
            </div>
          )}
        </Stack>
      </div>
      <div className={styles.stickyFooter}>
        <WizardInsuranceFooter insurance={insurance} onInsuranceChange={onInsuranceChange} zIndex={zIndex} />
        <WizardCostBreakdown selectedRate={selectedRate} />
        <div className={styles.footer}>
          <Text c='dimmed' size='xs'>
            <FormattedMessage {...wizardRatesPanelMessages.poweredBy} />
          </Text>
        </div>
      </div>
    </div>
  );
};
